// src/components/MonthOverMonthPricingChecks.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Grid,
  Alert
} from "@mui/material";
import api, { getMonthOverMonthPricingChecks, getMonthOverMonthPricingNotes, getPricingEntries, getPricingDates, saveMonthOverMonthPricingNotes } from "../api";
import { useCanWritePricing, PRICING_WRITE, NO_PRICING_WRITE_MESSAGE } from "../permissions";
import {
  DateTimeRenderer,
  useGridOperations,
  useSummaryStats,
  GridSummaryCards,
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  countWhere,
  isValueNull,
  isPermissionDenied,
  getPermissionDeniedText,
  getPermissionDeniedMessage,
  RequestAccessButton,
  PermissionTooltip,
  getWriteErrorMessage,
  isVersionConflict,
  loadedModifiedDate,
} from "@1823-partners/core";
import { formatDate } from "../utils/dateUtils";

// A pricing row carries a mark when PRICE is set at all; 0 is a valid mark
// (validation allows it), so only an empty/absent PRICE is skipped.
const hasPriceValue = (price) => price !== '' && price !== null && price !== undefined;

// Mean of the finite marks, or null when there are none (never 0 or NaN).
const meanOrNull = (prices) =>
  prices.length > 0 ? prices.reduce((sum, price) => sum + price, 0) / prices.length : null;

// Keep a present value (0 included); only an absent or empty one becomes null.
const presentOrNull = (value) => (hasPriceValue(value) ? value : null);

// A price rounded to 4 decimals for equality checks, or null when there is no
// finite price. Applied to both sides alike.
const roundPrice4 = (value) => {
  if (!hasPriceValue(value)) return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? Math.round(n * 10000) / 10000 : null;
};

const MonthOverMonthPricingChecks = ({ userId }) => {
  const canWritePricing = useCanWritePricing();
  const [currentDate, setCurrentDate] = useState('');
  const [previousDate, setPreviousDate] = useState('');
  // The dates on screen now, for async work started under other dates, and
  // the load generation that lets only the latest comparison load land.
  const currentDateRef = useRef(currentDate);
  currentDateRef.current = currentDate;
  const previousDateRef = useRef(previousDate);
  previousDateRef.current = previousDate;
  const loadGenerationRef = useRef(0);
  const [availableDates, setAvailableDates] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState(null);
  // The denial *object* alongside the message: RequestAccessButton reads
  // suggested_roles off it, which the message string cannot carry. Cleared
  // wherever `error` is cleared or replaced, or a later unrelated failure would
  // still show a button naming a role from an earlier denial.
  const [deniedError, setDeniedError] = useState(null);

  // Every replacement of the action banner advances this token, and a delayed clear
  // only removes the generation that scheduled it — so a pending "No changes" timer
  // cannot erase a later permission denial. Same approach as PricingGrid,
  // BrokersGrid and PricingFrequency; here it is for consistency rather than a
  // behaviour change, since the toolbar disables Save under exactly the condition
  // that schedules the timer.
  const actionErrorGenerationRef = useRef(0);

  const clearActionError = useCallback(() => {
    actionErrorGenerationRef.current += 1;
    setError(null);
    setDeniedError(null);
  }, []);

  const showActionError = useCallback((message, denial = null) => {
    actionErrorGenerationRef.current += 1;
    setError(message);
    setDeniedError(denial);
  }, []);

  const showTemporaryActionError = useCallback((message, timeoutMs) => {
    const generation = actionErrorGenerationRef.current + 1;
    actionErrorGenerationRef.current = generation;
    setError(message);
    setDeniedError(null);
    setTimeout(() => {
      if (actionErrorGenerationRef.current !== generation) return;
      actionErrorGenerationRef.current += 1;
      setError(null);
      setDeniedError(null);
    }, timeoutMs);
  }, []);
  const [successMessage, setSuccessMessage] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  // Unsaved note edits by ID2. The ref mirrors the state synchronously, so a
  // load or save that finishes later reads the edits tracked at that moment
  // rather than the ones its closure captured.
  const [updatedRows, setUpdatedRowsState] = useState({});
  const updatedRowsRef = useRef({});
  const setUpdatedRows = useCallback((next) => {
    const value = typeof next === 'function' ? next(updatedRowsRef.current) : next;
    updatedRowsRef.current = value;
    setUpdatedRowsState(value);
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  // Note editing is frozen from the Save click until the save settles: the
  // save replaces the MODIFIED_DATE an edit made meanwhile would carry, so that
  // edit's own save would be a false version conflict. The ref is set
  // synchronously, ahead of the isSaving render. While the save flushes an
  // editor that was still open, that one commit is let through.
  const isSavingRef = useRef(false);
  const isFlushingEditRef = useRef(false);
  const [filterSourceDifference, setFilterSourceDifference] = useState(false);
  const [filterPriceDifference, setFilterPriceDifference] = useState(false);

  // Use 1823-core grid operations hook
  const { gridRef } = useGridOperations();

  // Per-user grid layout persistence. Own the hook here (rather than letting
  // GridConfigMenu instantiate it) so we can bridge gridConfigs.onGridReady to
  // PamGrid — without that bridge a slow grid mount can miss the default-layout
  // auto-apply. See useGridConfigs / ReconList for the race rationale.
  const gridConfigs = useGridConfigs(api, {
    app: 'pricing',
    gridId: 'mom-checks',
    gridApiRef: gridRef,
  });

  const gridConfigComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  // Use 1823-core summary stats hook for summary calculations
  const { stats: summaryStats } = useSummaryStats({
    rowData: comparisonData,
    calculations: {
      totalSecurities: {
        label: 'Total Securities',
        calculate: (data) => data.length,
        color: 'primary'
      },
      priceChanges: {
        label: 'Price Changes',
        calculate: countWhere(item =>
          item.STATUS === 'Price Increased' || item.STATUS === 'Price Decreased'
        ),
        color: 'warning'
      },
      newSecurities: {
        label: 'New Securities',
        calculate: countWhere(item => item.STATUS === 'New Security'),
        color: 'info'
      },
      missingSecurities: {
        label: 'Missing Securities',
        calculate: countWhere(item => item.STATUS === 'Missing Security'),
        color: 'error'
      },
      multipleSources: {
        label: 'Multiple Sources',
        calculate: countWhere(item => item.STATUS === 'Multiple Price Sources'),
        color: 'secondary'
      },
      averagePriceChange: {
        label: 'Avg Change %',
        calculate: (data) => {
          const priceChangeValues = data
            .filter(item => item.PRICE_CHANGE_PERCENT !== null)
            .map(item => Math.abs(item.PRICE_CHANGE_PERCENT));
          return priceChangeValues.length > 0
            ? priceChangeValues.reduce((sum, val) => sum + val, 0) / priceChangeValues.length
            : 0;
        },
        format: (v) => `${v.toFixed(2)}%`,
        color: 'secondary'
      }
    }
  });

  // Load available dates
  useEffect(() => {
    const loadDates = async () => {
      try {
        setIsLoadingDates(true);
        setError(null);
        setDeniedError(null);
        const response = await getPricingDates();
        const dates = response.data || [];
        setAvailableDates(dates.sort((a, b) => new Date(b) - new Date(a))); // Sort descending

        // Auto-select the two most recent dates
        if (dates.length >= 2) {
          const sortedDates = dates.sort((a, b) => new Date(b) - new Date(a));
          setCurrentDate(sortedDates[0]);
          setPreviousDate(sortedDates[1]);
        }
      } catch (error) {
        console.error("Error fetching dates:", error);
        if (isPermissionDenied(error)) {
          setError(getPermissionDeniedMessage(error));
        } else {
          setError("Failed to load available dates. Please try again.");
          setDeniedError(null);
        }
      } finally {
        setIsLoadingDates(false);
      }
    };

    loadDates();
  }, []);

  // Helper function to calculate source type based on ID2 and broker count
  const calculateSourceType = (id2, brokerCount) => {
    if (id2.includes('-U')) {
      return 'Unfunded Commit';
    } else if (brokerCount > 1) {
      return 'Multi Broker';
    } else {
      return '1 Broker';
    }
  };

  // Apply filters to the comparison data
  const applyFilters = useCallback((data) => {
    if (!filterSourceDifference && !filterPriceDifference) {
      return data;
    }

    return data.filter(row => {
      let passesFilters = true;

      // Filter 1: MoM Current Source is not equal to Current Source
      if (filterSourceDifference) {
        const momCurrentSource = row['MOM_CURRENT_SOURCE'];
        const currentSource = row['CURRENT_SOURCE'];
        if (momCurrentSource === currentSource) {
          passesFilters = false;
        }
      }

      // Filter 2: MoM Current Price is not equal to Current Price (up to 4 decimal places)
      if (filterPriceDifference && passesFilters) {
        const momCurrentPrice = row['MOM_CURRENT_PRICE'];
        const currentPrice = row['CURRENT_MEAN_PRICE'];

        // Round both prices to 4 decimal places for comparison. A 0 mark is a
        // price, so it is compared as one; two missing prices are equal, and a
        // missing price differs from any present one.
        const momPriceRounded = roundPrice4(momCurrentPrice);
        const currentPriceRounded = roundPrice4(currentPrice);

        if (momPriceRounded === currentPriceRounded) {
          passesFilters = false;
        }
      }

      return passesFilters;
    });
  }, [filterSourceDifference, filterPriceDifference]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilterSourceDifference(false);
    setFilterPriceDifference(false);
  }, []);

  // Update filtered data when comparison data or filters change
  useEffect(() => {
    const filtered = applyFilters(comparisonData);
    setFilteredData(filtered);
  }, [comparisonData, applyFilters]);

  // Load and compare pricing data from three sources.
  //
  // Only the latest load for the dates on screen may touch state: each load
  // takes a generation, and a response whose generation is no longer current
  // (the user picked other dates, or a newer load started) is dropped, so a
  // slow earlier request never shows one date pair's rows under another. A
  // call made from a stale closure (e.g. a save that finished after the dates
  // changed) for dates no longer selected does not start at all, so it cannot
  // supersede the load for the dates that are.
  //
  // Unsaved notes still tracked when the load lands are put back on the
  // reloaded rows. Resolves to those rows when this load is the one applied,
  // otherwise to undefined.
  const loadComparisonData = useCallback(async () => {
    if (!currentDate || !previousDate || currentDate === previousDate) return undefined;
    if (currentDate !== currentDateRef.current || previousDate !== previousDateRef.current) return undefined;

    const generation = ++loadGenerationRef.current;
    const isCurrent = () => generation === loadGenerationRef.current;
    try {
      setIsLoadingData(true);
      setError(null);
      setDeniedError(null);

      // Fetch data from all four sources in parallel
      const [momResponse, currentPricingResponse, previousPricingResponse, notesResponse] = await Promise.all([
        getMonthOverMonthPricingChecks(currentDate, previousDate),
        getPricingEntries(currentDate),
        getPricingEntries(previousDate),
        getMonthOverMonthPricingNotes(currentDate)
      ]);
      if (!isCurrent()) return undefined;

      const momData = momResponse.data || [];
      const currentPricingData = currentPricingResponse.data || [];
      const previousPricingData = previousPricingResponse.data || [];
      const notesData = notesResponse.data || [];

      // Create maps for easier lookup using ID2 as key
      const momMap = new Map();
      const currentPricingMap = new Map();
      const previousPricingMap = new Map();
      const notesMap = new Map();

      // Process MoM data
      momData.forEach(entry => {
        if (entry.ID2) {
          const key = entry.ID2; // MoM data doesn't have broker, use ID2 only
          momMap.set(key, {
            ID2: entry.ID2,
            DESCRIPTION: entry.DES,
            CLIENT: entry.CLIENT,
            SEC_OWNER: entry.SEC_OWNER,
            MOM_CURRENT_PRICE: entry.CURRENTPX,
            MOM_CURRENT_SOURCE: entry.CURRENTSOURCE,
            MOM_CURRENT_SOURCE2: entry.CURRENTSOURCE2,
            MOM_PRIOR_PRICE: entry.PRIORPX,
            MOM_PRIOR_SOURCE: entry.PRIORSOURCE,
            MOM_PRIOR_SOURCE2: entry.PRIORSOURCE2,
            MOM_PRIOR_DATE: entry.PRIORDT,
            CHECK1: entry.CHECK1,
            ASOFDT: entry.ASOFDT,
            ST: entry.ST,
            AT1: entry.AT1,
            AT2: entry.AT2,
            AT3: entry.AT3,
            MOM_NOTES: entry.MOM_NOTES
          });
        }
      });

      // Process current pricing data - filter for USE_PRICE = 'Y' and calculate mean prices by ID2
      const currentPricingFiltered = currentPricingData.filter(entry =>
        entry.USE_PRICE === 'Y' || entry.USE_PRICE === true
      );

      const currentPricingGrouped = {};
      currentPricingFiltered.forEach(entry => {
        if (entry.ID2 && hasPriceValue(entry.PRICE)) {
          const id2 = entry.ID2;
          if (!currentPricingGrouped[id2]) {
            currentPricingGrouped[id2] = {
              prices: [],
              brokers: [],
              brokerPricePairs: [],
              date: formatDate(entry.ASOFDT)
            };
          }
          const price = parseFloat(entry.PRICE);
          if (!isNaN(price)) {
            currentPricingGrouped[id2].prices.push(price);
            currentPricingGrouped[id2].brokers.push(entry.BROKER);
            currentPricingGrouped[id2].brokerPricePairs.push({
              broker: entry.BROKER,
              price: price
            });
          }
        }
      });

      // Calculate mean prices for current data and organize individual broker prices
      Object.keys(currentPricingGrouped).forEach(id2 => {
        const group = currentPricingGrouped[id2];
        const meanPrice = meanOrNull(group.prices);
        const uniqueBrokers = [...new Set(group.brokers)];

        // Sort broker-price pairs alphabetically by broker name
        const sortedBrokerPricePairs = group.brokerPricePairs.sort((a, b) => a.broker.localeCompare(b.broker));

        // Extract individual broker and price data (up to 3 brokers)
        const currentBroker1 = sortedBrokerPricePairs[0]?.broker || null;
        const currentPrice1 = sortedBrokerPricePairs[0]?.price ?? null;
        const currentBroker2 = sortedBrokerPricePairs[1]?.broker || null;
        const currentPrice2 = sortedBrokerPricePairs[1]?.price ?? null;
        const currentBroker3 = sortedBrokerPricePairs[2]?.broker || null;
        const currentPrice3 = sortedBrokerPricePairs[2]?.price ?? null;

        currentPricingMap.set(id2, {
          ID2: id2,
          MEAN_PRICE: meanPrice,
          DATE: group.date,
          BROKERS: uniqueBrokers,
          BROKER_COUNT: uniqueBrokers.length,
          SOURCE: calculateSourceType(id2, uniqueBrokers.length),
          CURRENT_BROKER_1: currentBroker1,
          CURRENT_PRICE_1: currentPrice1,
          CURRENT_BROKER_2: currentBroker2,
          CURRENT_PRICE_2: currentPrice2,
          CURRENT_BROKER_3: currentBroker3,
          CURRENT_PRICE_3: currentPrice3
        });
      });

      // Process previous pricing data - filter for USE_PRICE = 'Y' and calculate mean prices by ID2
      const previousPricingFiltered = previousPricingData.filter(entry =>
        entry.USE_PRICE === 'Y' || entry.USE_PRICE === true
      );

      const previousPricingGrouped = {};
      previousPricingFiltered.forEach(entry => {
        if (entry.ID2 && hasPriceValue(entry.PRICE)) {
          const id2 = entry.ID2;
          if (!previousPricingGrouped[id2]) {
            previousPricingGrouped[id2] = {
              prices: [],
              brokers: [],
              brokerPricePairs: [],
              date: formatDate(entry.ASOFDT)
            };
          }
          const price = parseFloat(entry.PRICE);
          if (!isNaN(price)) {
            previousPricingGrouped[id2].prices.push(price);
            previousPricingGrouped[id2].brokers.push(entry.BROKER);
            previousPricingGrouped[id2].brokerPricePairs.push({
              broker: entry.BROKER,
              price: price
            });
          }
        }
      });

      // Calculate mean prices for previous data and organize individual broker prices
      Object.keys(previousPricingGrouped).forEach(id2 => {
        const group = previousPricingGrouped[id2];
        const meanPrice = meanOrNull(group.prices);
        const uniqueBrokers = [...new Set(group.brokers)];

        // Sort broker-price pairs alphabetically by broker name
        const sortedBrokerPricePairs = group.brokerPricePairs.sort((a, b) => a.broker.localeCompare(b.broker));

        // Extract individual broker and price data (up to 3 brokers)
        const previousBroker1 = sortedBrokerPricePairs[0]?.broker || null;
        const previousPrice1 = sortedBrokerPricePairs[0]?.price ?? null;
        const previousBroker2 = sortedBrokerPricePairs[1]?.broker || null;
        const previousPrice2 = sortedBrokerPricePairs[1]?.price ?? null;
        const previousBroker3 = sortedBrokerPricePairs[2]?.broker || null;
        const previousPrice3 = sortedBrokerPricePairs[2]?.price ?? null;

        previousPricingMap.set(id2, {
          ID2: id2,
          MEAN_PRICE: meanPrice,
          DATE: group.date,
          BROKERS: uniqueBrokers,
          BROKER_COUNT: uniqueBrokers.length,
          SOURCE: calculateSourceType(id2, uniqueBrokers.length),
          PREVIOUS_BROKER_1: previousBroker1,
          PREVIOUS_PRICE_1: previousPrice1,
          PREVIOUS_BROKER_2: previousBroker2,
          PREVIOUS_PRICE_2: previousPrice2,
          PREVIOUS_BROKER_3: previousBroker3,
          PREVIOUS_PRICE_3: previousPrice3
        });
      });

      // Process notes data
      notesData.forEach(entry => {
        if (entry.ID2) {
          notesMap.set(entry.ID2, {
            ID2: entry.ID2,
            NOTES: entry.NOTES || '',
            MODIFIED_BY: entry.MODIFIED_BY || '',
            // The loaded token, exactly as the API returned it.
            MODIFIED_DATE: loadedModifiedDate(entry)
          });
        }
      });

      // Get all unique ID2s from all four data sources
      const allId2s = new Set([
        ...currentPricingMap.keys(),
        ...previousPricingMap.keys(),
        ...momMap.keys(),
        ...notesMap.keys()
      ]);

      // Merge data from all four sources using ID2 only
      const mergedData = [];

      allId2s.forEach(id2 => {
        const currentEntry = currentPricingMap.get(id2);
        const previousEntry = previousPricingMap.get(id2);
        const momEntry = momMap.get(id2);
        const notesEntry = notesMap.get(id2);

        // Calculate price changes from mean pricing data
        const currentPrice = currentEntry?.MEAN_PRICE;
        const previousPrice = previousEntry?.MEAN_PRICE;
        const pricesExist = !isValueNull(currentPrice) && !isValueNull(previousPrice);
        const priceChange = pricesExist ? currentPrice - previousPrice : null;
        // A previous mark of 0 has no percentage change (it would be ±Infinity).
        const priceChangePercent = pricesExist && previousPrice !== 0 ? (priceChange / previousPrice) * 100 : null;

        // Determine status
        let status = 'No Change';
        if (momEntry?.CHECK1 === 'MULTIPLE PX SRC') {
          status = 'Multiple Price Sources';
        } else if (!previousEntry) {
          status = 'New Security';
        } else if (!currentEntry) {
          status = 'Missing Security';
        } else if (isValueNull(currentPrice)) {
          status = 'Missing Current Price';
        } else if (isValueNull(previousPrice)) {
          status = 'Missing Previous Price';
        } else if (priceChange > 0) {
          status = 'Price Increased';
        } else if (priceChange < 0) {
          status = 'Price Decreased';
        }

        mergedData.push({
          ID2: id2,
          DESCRIPTION: momEntry?.DESCRIPTION,

          // MoM API data (prefixed with MOM_)
          MOM_CLIENT: momEntry?.CLIENT || null,
          MOM_SEC_OWNER: momEntry?.SEC_OWNER || null,
          MOM_CURRENT_PRICE: presentOrNull(momEntry?.MOM_CURRENT_PRICE),
          MOM_CURRENT_SOURCE: momEntry?.MOM_CURRENT_SOURCE || null,
          MOM_CURRENT_SOURCE2: momEntry?.MOM_CURRENT_SOURCE2 || null,
          MOM_PRIOR_PRICE: presentOrNull(momEntry?.MOM_PRIOR_PRICE),
          MOM_PRIOR_SOURCE: momEntry?.MOM_PRIOR_SOURCE || null,
          MOM_PRIOR_SOURCE2: momEntry?.MOM_PRIOR_SOURCE2 || null,
          MOM_CHECK1: momEntry?.CHECK1 || null,
          MOM_ST: momEntry?.ST || null,
          MOM_AT1: momEntry?.AT1 || null,
          MOM_AT2: momEntry?.AT2 || null,
          MOM_AT3: momEntry?.AT3 || null,

          // Current date pricing data - mean prices and broker info
          CURRENT_DATE: currentEntry?.DATE || null,
          CURRENT_MEAN_PRICE: currentPrice,
          CURRENT_BROKER_COUNT: currentEntry?.BROKER_COUNT || null,
          CURRENT_BROKERS: currentEntry?.BROKERS || null,
          CURRENT_SOURCE: currentEntry?.SOURCE || null,

          // Previous date pricing data - mean prices and broker info
          PREVIOUS_DATE: previousEntry?.DATE || null,
          PREVIOUS_MEAN_PRICE: previousPrice,
          PREVIOUS_BROKER_COUNT: previousEntry?.BROKER_COUNT || null,
          PREVIOUS_BROKERS: previousEntry?.BROKERS || null,
          PREVIOUS_SOURCE: previousEntry?.SOURCE || null,

          // Current individual broker and price data
          CURRENT_BROKER_1: currentEntry?.CURRENT_BROKER_1 || null,
          CURRENT_PRICE_1: currentEntry?.CURRENT_PRICE_1 ?? null,
          CURRENT_BROKER_2: currentEntry?.CURRENT_BROKER_2 || null,
          CURRENT_PRICE_2: currentEntry?.CURRENT_PRICE_2 ?? null,
          CURRENT_BROKER_3: currentEntry?.CURRENT_BROKER_3 || null,
          CURRENT_PRICE_3: currentEntry?.CURRENT_PRICE_3 ?? null,

          // Previous individual broker and price data
          PREVIOUS_BROKER_1: previousEntry?.PREVIOUS_BROKER_1 || null,
          PREVIOUS_PRICE_1: previousEntry?.PREVIOUS_PRICE_1 ?? null,
          PREVIOUS_BROKER_2: previousEntry?.PREVIOUS_BROKER_2 || null,
          PREVIOUS_PRICE_2: previousEntry?.PREVIOUS_PRICE_2 ?? null,
          PREVIOUS_BROKER_3: previousEntry?.PREVIOUS_BROKER_3 || null,
          PREVIOUS_PRICE_3: previousEntry?.PREVIOUS_PRICE_3 ?? null,

          // Calculated comparison fields
          PRICE_CHANGE: priceChange,
          PRICE_CHANGE_PERCENT: priceChangePercent,
          STATUS: status,

          // User notes from notes API (prioritize over MOM_NOTES)
          NOTES: notesEntry?.NOTES || '',
          MODIFIED_BY: notesEntry?.MODIFIED_BY || '',
          // The loaded note's MODIFIED_DATE token (null when the CUSIP has no note yet).
          MODIFIED_DATE: loadedModifiedDate(notesEntry)
        });
      });

      // Unsaved notes belong to the current date and ID2, not to the previous
      // date, so they stay on the rows across a reload (the loaded
      // MODIFIED_DATE stays the row's own; the edit keeps the one it began from).
      const pending = updatedRowsRef.current;
      const rows = mergedData
        .sort((a, b) => a.ID2.localeCompare(b.ID2))
        .map(row => (pending[row.ID2]?.NOTES !== undefined ? { ...row, NOTES: pending[row.ID2].NOTES } : row));

      // Set the merged data - summary stats are calculated automatically by useSummaryStats hook
      setComparisonData(rows);
      return rows;

    } catch (error) {
      if (!isCurrent()) return undefined;
      console.error("Error loading comparison data:", error);
      if (isPermissionDenied(error)) {
        setError(getPermissionDeniedMessage(error));
      } else {
        setError("Failed to load pricing comparison. Please try again.");
        setDeniedError(null);
      }
    } finally {
      if (isCurrent()) setIsLoadingData(false);
    }
  }, [currentDate, previousDate]);

  // The load for the dates on screen now, for a save that finishes after the
  // previous date changed: its own closure's load is for the old pair and
  // would not run.
  const loadComparisonDataRef = useRef(loadComparisonData);
  loadComparisonDataRef.current = loadComparisonData;

  // A note is saved under the current date and ID2, so a current-date change
  // drops unsaved notes rather than writing them under the newly selected
  // date. A previous-date change keeps them: the reload puts them back on the
  // new pair's rows. Declared before the load effect so the drop is seen by
  // the load it triggers.
  useEffect(() => {
    setUpdatedRows({});
  }, [currentDate, setUpdatedRows]);

  // Load data when dates change.
  useEffect(() => {
    if (currentDate && previousDate && currentDate !== previousDate) {
      loadComparisonData();
    } else {
      // No comparable pair: drop any in-flight load for the previous pair.
      loadGenerationRef.current += 1;
      setIsLoadingData(false);
    }
  }, [currentDate, previousDate, loadComparisonData]);

  // A response that lands after unmount must not touch state.
  useEffect(() => () => {
    loadGenerationRef.current += 1;
  }, []);

  // Handle cell value changes to track modifications
  const onCellValueChanged = useCallback((event) => {
    const rowId = event.data.ID2;
    const field = event.colDef.field;
    const newValue = event.newValue;
    const oldValue = event.oldValue;

    // A change the grid reports while a save is running (the column is not
    // editable then, so only a late or programmatic one) is not tracked. The
    // grid already wrote it into the row object, so put the old value back
    // there too, or the cell would show a note that is never saved.
    if (isSavingRef.current && !isFlushingEditRef.current) {
      if (newValue !== oldValue && event.data) {
        event.data[field] = oldValue;
        event.api?.refreshCells?.({ rowNodes: event.node ? [event.node] : undefined, columns: [field], force: true });
      }
      return;
    }

    if (newValue !== oldValue) {
      setUpdatedRows(prev => {
        // MODIFIED_BY / MODIFIED_DATE are never restamped here: MODIFIED_DATE is
        // the concurrency token and the writer stamps both.
        const tracked = prev[rowId];
        const updateData = {
          ...tracked,
          [field]: newValue,
          ID2: rowId,
          _isModified: true,
          // The token this edit began from, kept with the edit: a later reload
          // (e.g. a previous-date change) may load a newer one, and sending that
          // would overwrite someone else's note without a conflict.
          _loadedModifiedDate: tracked ? tracked._loadedModifiedDate : loadedModifiedDate(event.data)
        };

        return {
          ...prev,
          [rowId]: updateData
        };
      });

      // If NOTES field is being edited, also update the row data immediately to show the changes in the grid
      if (field === 'NOTES') {
        setComparisonData(prevData =>
          prevData.map(row =>
            row.ID2 === rowId
              ? { ...row, [field]: newValue }
              : row
          )
        );
      }
    }
  }, [setUpdatedRows]);

  // Save modified rows
  const handleSave = useCallback(async () => {
    if (isSavingRef.current) return;
    // The whole banner, so nothing from a previous action survives this one.
    clearActionError();

    // The notes are saved under this current date; everything that runs after
    // the save checks it is still the one on screen.
    const saveDate = currentDate;
    // The edits this save writes, snapshotted once the open editor is flushed.
    let savedEdits = {};
    let modifiedRowIds = [];

    // Freeze editing before anything else (see isSavingRef).
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      // Commit an editor still open, so its note is part of this save. The grid
      // reports a committed value asynchronously (on its next timer turn), so
      // wait one turn for that report before taking the snapshot.
      const gridApi = gridRef.current?.api;
      if (gridApi?.getEditingCells?.().length > 0) {
        isFlushingEditRef.current = true;
        gridApi.stopEditing();
        await new Promise(resolve => setTimeout(resolve, 0));
        isFlushingEditRef.current = false;
        // A current-date switch in that turn dropped the edits with their date.
        if (currentDateRef.current !== saveDate) return;
      }

      savedEdits = updatedRowsRef.current;
      modifiedRowIds = Object.keys(savedEdits);

      if (modifiedRowIds.length === 0) {
        showTemporaryActionError('No changes to save.', 3000);
        return;
      }

      clearActionError();
      setSuccessMessage(null);

      // Prepare data for saving - only include the required fields for the endpoint.
      // A tracked edit is saved even when a previous-date change left its row off
      // screen: the note is keyed by the current date and ID2 alone.
      const modifiedRows = modifiedRowIds.map(rowId => {
        const updatedRow = savedEdits[rowId];
        const currentRowData = comparisonData.find(row => row.ID2 === rowId);

        // Only send the fields required by the saveMonthOverMonthPricingNotes endpoint
        return {
          ASOFDT: saveDate,
          ID2: rowId,
          NOTES: updatedRow.NOTES !== undefined ? updatedRow.NOTES : currentRowData?.NOTES || '',
          // The MODIFIED_DATE the edit began from, unchanged (null for a new note).
          MODIFIED_DATE: updatedRow._loadedModifiedDate ?? null
        };
      });

      await saveMonthOverMonthPricingNotes(modifiedRows);

      // After a current-date change these edits were already dropped, and the
      // old pair's reload would not belong on screen.
      if (currentDateRef.current === saveDate) {
        // Drop the edits this save wrote. Editing was frozen while it ran, so
        // no newer edit can be tracked on an old MODIFIED_DATE; the identity
        // check is only a backstop.
        setUpdatedRows(prev => Object.fromEntries(
          Object.entries(prev).filter(([rowId, row]) => row !== savedEdits[rowId])
        ));

        // Reload the dates on screen now (the previous date may have changed
        // while the save was in flight) to reflect any server-side changes.
        await loadComparisonDataRef.current();
      }

      setSuccessMessage(`Successfully saved ${modifiedRows.length} modified rows.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error saving data:', error);
      if (isPermissionDenied(error)) {
        showActionError(getPermissionDeniedText(error), error);
      } else {
        // Only for the same current date: if the user switched it while the save
        // was in flight, these notes were dropped and do not belong to the new rows.
        if (isVersionConflict(error) && currentDateRef.current === saveDate) {
          // Reload the dates on screen (it clears the banner); the reload puts the
          // still-tracked notes back on the rows so the user can re-apply them.
          // Never retry automatically.
          const reloadedRows = await loadComparisonDataRef.current();
          // Re-applying is a deliberate save against the reloaded MODIFIED_DATE,
          // so the conflicted edits take the reloaded rows' tokens. A reload that
          // was superseded or failed leaves them as they were (the next save
          // conflicts again rather than overwriting).
          if (reloadedRows && currentDateRef.current === saveDate) {
            const reloadedById = new Map(reloadedRows.map(row => [row.ID2, row]));
            setUpdatedRows(prev => {
              const next = { ...prev };
              modifiedRowIds.forEach(rowId => {
                const reloaded = reloadedById.get(rowId);
                // Only an edit that still began from the token that conflicted.
                const sameEdit = next[rowId]?._loadedModifiedDate === savedEdits[rowId]._loadedModifiedDate;
                if (next[rowId] && sameEdit && reloaded) {
                  next[rowId] = { ...next[rowId], _loadedModifiedDate: loadedModifiedDate(reloaded) };
                }
              });
              return next;
            });
          }
        }
        showActionError(getWriteErrorMessage(error, 'Failed to save changes. Please try again.'));
      }
    } finally {
      isFlushingEditRef.current = false;
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [comparisonData, currentDate, gridRef, setUpdatedRows, clearActionError, showActionError, showTemporaryActionError]);

  const columnDefs = useMemo(() => [
    {
      headerName: "ID2",
      field: "ID2",
      pinned: 'left',
      minWidth: 150,
      sort: 'asc'
    },
    {
      headerName: "Description",
      field: "DESCRIPTION",
      pinned: 'left',
      minWidth: 250
    },
    {
      headerName: "Status",
      field: "STATUS",
      minWidth: 180,
      cellStyle: params => {
        switch (params.value) {
          case 'Price Increased':
            return { backgroundColor: '#e8f5e8', color: '#2e7d32' };
          case 'Price Decreased':
            return { backgroundColor: '#ffebee', color: '#c62828' };
          case 'New Security':
            return { backgroundColor: '#e3f2fd', color: '#1565c0' };
          case 'Missing Security':
            return { backgroundColor: '#fff3e0', color: '#ef6c00' };
          case 'Missing Current Price':
            return { backgroundColor: '#ffebee', color: '#c62828' };
          case 'Missing Previous Price':
            return { backgroundColor: '#ffebee', color: '#c62828' };
          case 'Multiple Price Sources':
            return { backgroundColor: '#fce4ec', color: '#ad1457' };
          default:
            return {};
        }
      }
    },

    // MoM API Data Section
    {
      headerName: "MoM Client",
      field: "MOM_CLIENT",
      headerClass: 'mom-header',
      minWidth: 100
    },
    {
      headerName: "MoM Sec Owner",
      field: "MOM_SEC_OWNER",
      headerClass: 'mom-header',
      minWidth: 100
    },
    {
      headerName: "MoM Current Price",
      field: "MOM_CURRENT_PRICE",
      headerClass: 'mom-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 130
    },
    {
      headerName: "MoM Current Source",
      field: "MOM_CURRENT_SOURCE",
      headerClass: 'mom-header',
      minWidth: 150
    },
    {
      headerName: "MoM Prior Price",
      field: "MOM_PRIOR_PRICE",
      headerClass: 'mom-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 130
    },
    {
      headerName: "MoM Prior Source",
      field: "MOM_PRIOR_SOURCE",
      headerClass: 'mom-header',
      minWidth: 150
    },
    {
      headerName: "MoM Check",
      field: "MOM_CHECK1",
      headerClass: 'mom-header',
      minWidth: 150
    },

    // Current Pricing Data Section - Mean Prices and Broker Info
    {
      headerName: "Current Date",
      field: "CURRENT_DATE",
      headerClass: 'current-header',
      valueFormatter: params => params.value ? formatDate(params.value) : 'N/A',
      minWidth: 120
    },
    {
      headerName: "Current Mean Price",
      field: "CURRENT_MEAN_PRICE",
      headerClass: 'current-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 150
    },
    {
      headerName: "Current Broker Count",
      field: "CURRENT_BROKER_COUNT",
      headerClass: 'current-header',
      type: 'numericColumn',
      minWidth: 130
    },
    {
      headerName: "Current Source",
      field: "CURRENT_SOURCE",
      headerClass: 'current-header',
      minWidth: 200
    },

    // Previous Pricing Data Section - Mean Prices and Broker Info
    {
      headerName: "Previous Date",
      field: "PREVIOUS_DATE",
      headerClass: 'previous-header',
      valueFormatter: params => params.value ? formatDate(params.value) : 'N/A',
      minWidth: 120
    },
    {
      headerName: "Previous Mean Price",
      field: "PREVIOUS_MEAN_PRICE",
      headerClass: 'previous-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 150
    },
    {
      headerName: "Previous Broker Count",
      field: "PREVIOUS_BROKER_COUNT",
      headerClass: 'previous-header',
      type: 'numericColumn',
      minWidth: 130
    },
    {
      headerName: "Previous Source",
      field: "PREVIOUS_SOURCE",
      headerClass: 'previous-header',
      minWidth: 200
    },

    // Current Broker Data Section
    {
      headerName: "Current Broker 1",
      field: "CURRENT_BROKER_1",
      headerClass: 'current-header',
      minWidth: 120
    },
    {
      headerName: "Current Price 1",
      field: "CURRENT_PRICE_1",
      headerClass: 'current-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 120
    },
    {
      headerName: "Current Broker 2",
      field: "CURRENT_BROKER_2",
      headerClass: 'current-header',
      minWidth: 120
    },
    {
      headerName: "Current Price 2",
      field: "CURRENT_PRICE_2",
      headerClass: 'current-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 120
    },
    {
      headerName: "Current Broker 3",
      field: "CURRENT_BROKER_3",
      headerClass: 'current-header',
      minWidth: 120
    },
    {
      headerName: "Current Price 3",
      field: "CURRENT_PRICE_3",
      headerClass: 'current-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 120
    },

    // Previous Broker Data Section
    {
      headerName: "Previous Broker 1",
      field: "PREVIOUS_BROKER_1",
      headerClass: 'previous-header',
      minWidth: 120
    },
    {
      headerName: "Previous Price 1",
      field: "PREVIOUS_PRICE_1",
      headerClass: 'previous-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 120
    },
    {
      headerName: "Previous Broker 2",
      field: "PREVIOUS_BROKER_2",
      headerClass: 'previous-header',
      minWidth: 120
    },
    {
      headerName: "Previous Price 2",
      field: "PREVIOUS_PRICE_2",
      headerClass: 'previous-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 120
    },
    {
      headerName: "Previous Broker 3",
      field: "PREVIOUS_BROKER_3",
      headerClass: 'previous-header',
      minWidth: 120
    },
    {
      headerName: "Previous Price 3",
      field: "PREVIOUS_PRICE_3",
      headerClass: 'previous-header',
      type: 'numericColumn',
      valueFormatter: params => !isValueNull(params.value) ? parseFloat(params.value).toFixed(4) : 'N/A',
      minWidth: 120
    },

    // Comparison/Calculation Section
    {
      headerName: "Price Change",
      field: "PRICE_CHANGE",
      headerClass: 'comparison-header',
      type: 'numericColumn',
      valueFormatter: params => {
        if (isValueNull(params.value)) return 'N/A';
        const value = parseFloat(params.value);
        return value >= 0 ? `+${value.toFixed(4)}` : value.toFixed(4);
      },
      cellStyle: params => {
        if (isValueNull(params.value)) return {};
        const value = parseFloat(params.value);
        return value > 0
          ? { color: '#2e7d32', fontWeight: 'bold' }
          : value < 0
            ? { color: '#c62828', fontWeight: 'bold' }
            : {};
      },
      minWidth: 130
    },
    {
      headerName: "Change %",
      field: "PRICE_CHANGE_PERCENT",
      headerClass: 'comparison-header',
      type: 'numericColumn',
      valueFormatter: params => {
        if (isValueNull(params.value)) return 'N/A';
        const value = parseFloat(params.value);
        return value >= 0 ? `+${value.toFixed(2)}%` : `${value.toFixed(2)}%`;
      },
      cellStyle: params => {
        if (isValueNull(params.value)) return {};
        const value = parseFloat(params.value);
        return value > 0
          ? { color: '#2e7d32', fontWeight: 'bold' }
          : value < 0
            ? { color: '#c62828', fontWeight: 'bold' }
            : {};
      },
      minWidth: 120
    },

    // Additional MoM fields
    {
      headerName: "MoM Security Type",
      field: "MOM_ST",
      headerClass: 'mom-header',
      minWidth: 120
    },
    {
      headerName: "MoM Asset Type 1",
      field: "MOM_AT1",
      headerClass: 'mom-header',
      minWidth: 150
    },
    {
      headerName: "MoM Asset Type 2",
      field: "MOM_AT2",
      headerClass: 'mom-header',
      minWidth: 120
    },
    {
      headerName: "MoM Asset Type 3",
      field: "MOM_AT3",
      headerClass: 'mom-header',
      minWidth: 150
    },

    // Notes column
    {
      headerName: "Notes",
      field: "NOTES",
      // Read when an edit (or paste) starts, so the freeze applies from the
      // Save click on, before the isSaving render.
      editable: () => !isSavingRef.current,
      minWidth: 200,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        maxLength: 500
      },
      cellClass: (params) => {
        const rowId = params.data.ID2;
        return updatedRows[rowId]?.NOTES !== undefined ? 'ag-cell-modified' : '';
      }
    },
    {
      headerName: "Modified By",
      field: "MODIFIED_BY",
      editable: false,
      minWidth: 120,
      cellClass: (params) => {
        const rowId = params.data.ID2;
        return updatedRows[rowId]?.NOTES !== undefined ? 'ag-cell-modified' : '';
      }
    },
    {
      headerName: "Modified Date",
      field: "MODIFIED_DATE",
      editable: false,
      minWidth: 160,
      cellRenderer: DateTimeRenderer,
      cellClass: (params) => {
        const rowId = params.data.ID2;
        return updatedRows[rowId]?.NOTES !== undefined ? 'ag-cell-modified' : '';
      }
    }
  ], [updatedRows]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100
  }), []);

  const excelStyles = useMemo(() => [
    {
      id: 'dateFormat',
      dataType: 'DateTime',
      numberFormat: {
        format: 'yyyy-mm-dd'
      }
    },
    {
      id: 'priceFormat',
      dataType: 'Number',
      numberFormat: {
        format: '#,##0.0000'
      }
    },
    {
      id: 'percentFormat',
      dataType: 'Number',
      numberFormat: {
        format: '0.00%'
      }
    }
  ], []);

  if (isLoadingDates) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading available dates...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Month Over Month Pricing Checks
      </Typography>

      {/* Date Selection */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel id="current-date-label">Current Date</InputLabel>
            <Select
              labelId="current-date-label"
              value={currentDate}
              label="Current Date"
              onChange={(e) => setCurrentDate(e.target.value)}
              disabled={isLoadingData}
            >
              {availableDates.map((date) => (
                <MenuItem key={date} value={date}>
                  {date}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel id="previous-date-label">Previous Date</InputLabel>
            <Select
              labelId="previous-date-label"
              value={previousDate}
              label="Previous Date"
              onChange={(e) => setPreviousDate(e.target.value)}
              disabled={isLoadingData}
            >
              {availableDates.map((date) => (
                <MenuItem key={date} value={date}>
                  {date}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Summary Cards - using 1823-core GridSummaryCards */}
      {comparisonData.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <GridSummaryCards stats={summaryStats} columns={6} spacing={2} />
        </Box>
      )}

      {/* Error Message */}
      {error && (
        <Alert
          severity={deniedError ? 'warning' : 'error'}
          action={deniedError ? <RequestAccessButton error={deniedError} /> : undefined}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={loadComparisonData}
          disabled={isLoadingData || !currentDate || !previousDate}
        >
          {isLoadingData ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Refresh Comparison
        </Button>
        {/* PermissionTooltip, not a bare Tooltip: this Button has no wrapper of its own, and a
            disabled MUI Button has pointer-events:none, so a tooltip needs the span the component
            supplies. It also stays silent unless the denial is *known*, so a save disabled merely
            because nothing has changed does not claim a permission problem. */}
        <PermissionTooltip permission={PRICING_WRITE} title={NO_PRICING_WRITE_MESSAGE}>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={!canWritePricing || isSaving || Object.keys(updatedRows).length === 0}
        >
          {isSaving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Save Changes ({Object.keys(updatedRows).length})
        </Button>
        </PermissionTooltip>
        <Button
          variant={filterSourceDifference ? "contained" : "outlined"}
          color="warning"
          onClick={() => setFilterSourceDifference(!filterSourceDifference)}
          disabled={comparisonData.length === 0}
        >
          {filterSourceDifference ? "✓ " : ""}Source Differences
        </Button>
        <Button
          variant={filterPriceDifference ? "contained" : "outlined"}
          color="error"
          onClick={() => setFilterPriceDifference(!filterPriceDifference)}
          disabled={comparisonData.length === 0}
        >
          {filterPriceDifference ? "✓ " : ""}Price Differences
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={clearFilters}
          disabled={!filterSourceDifference && !filterPriceDifference}
        >
          Clear Filters
        </Button>
        <Typography variant="body2" sx={{ alignSelf: 'center', ml: 2, color: 'text.secondary' }}>
          Showing {filteredData.length} of {comparisonData.length} rows
        </Typography>
      </Box>

      {/* Comparison Grid - using 1823-core PamGrid */}
      <Box sx={{ position: 'relative' }}>
        <style>{`
          .ag-header-cell-label .mom-header {
            background-color: #e3f2fd !important;
            color: #1565c0 !important;
            font-weight: bold;
          }
          .ag-header-cell-label .current-header {
            background-color: #e8f5e8 !important;
            color: #2e7d32 !important;
            font-weight: bold;
          }
          .ag-header-cell-label .previous-header {
            background-color: #fff3e0 !important;
            color: #ef6c00 !important;
            font-weight: bold;
          }
          .ag-header-cell-label .comparison-header {
            background-color: #fce4ec !important;
            color: #ad1457 !important;
            font-weight: bold;
          }
          .ag-cell-modified {
            background-color: rgba(255, 165, 0, 0.2) !important;
          }
        `}</style>
        <PamGrid
          ref={gridRef}
          rowData={filteredData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          height="600px"
          isLoading={isLoadingData}
          loadingMessage="Loading pricing comparison..."
          showToolbar
          showExport
          exportConfig={{
            fileName: `MonthOverMonthPricingChecks_${currentDate}_vs_${previousDate}_${new Date().toISOString().split('T')[0]}.xlsx`,
            sheetName: 'Pricing Comparison',
            processCellCallback: (params) => {
              if (params.column.getColId() === 'ID2') {
                return params.value ? `'${params.value.toString()}` : '';
              }
              if (params.column.getColId().includes('PRICE') ||
                params.column.getColId().includes('CHANGE') ||
                params.column.getColId().includes('PERCENT')) {
                if (params.value !== null && params.value !== undefined) {
                  return parseFloat(params.value);
                }
              }
              return params.value;
            },
          }}
          getRowId={(params) => params.data ? params.data.ID2 : params.ID2}
          onCellValueChanged={onCellValueChanged}
          excelStyles={excelStyles}
          rowHeight={32}
          enableCellTextSelection={true}
          enableRangeSelection={true}
          ensureDomOrder={true}
          suppressHorizontalScroll={false}
          showFooter={true}
          // Grid layout persistence (view-config)
          customToolbarComponents={gridConfigComponents}
          onGridReady={gridConfigs.onGridReady}
          onFilterChanged={gridConfigs.onGridStateChanged}
          agGridProps={gridConfigs.stashBindings}
        />
      </Box>
    </Box>
  );
};

export default MonthOverMonthPricingChecks;